import { create } from "zustand";

export interface EquityPoint {
  date: string;
  value: number;
  benchmark?: number;
}

export interface AllocationData {
  date: string;
  allocations: Record<string, number>;
}

export interface TradeOrder {
  ticker: string;
  action: "BUY" | "SELL";
  shares: number;
  price: number;
  dollarAmount: number;
  currentWeight: number;
  targetWeight: number;
}

export interface TodayAllocation {
  date: string;
  positions: Record<string, number>;
  portfolioValue: number;
  trades: TradeOrder[];
}

export interface BacktestMetrics {
  cagr: number;
  sharpe: number;
  sortino: number;
  maxDrawdown: number;
  volatility: number;
  calmar?: number;
  bestYear: number;
  worstYear: number;
  totalReturn: number;
  tradingDays: number;
  // Trailing returns
  trailing1m?: number;
  trailing3m?: number;
  // Benchmark comparison
  alpha?: number;
  beta?: number;
  rSquared?: number;
  correlation?: number;
}

export interface BacktestResult {
  strategyName: string;
  strategyCode?: string;
  metrics: BacktestMetrics;
  equity: EquityPoint[];
  todayAllocation: TodayAllocation;
  allocations: AllocationData[];
  // Per-ticker daily returns aligned with equity[] dates (index i = equity[i].date)
  tickerReturns?: Record<string, number[]>;
  startDate: string;
  endDate: string;
  // Trading cost tracking
  initialCapital: number;
  finalValue: number;
  totalSlippage: number;
  totalRegulatoryFees: number;
}

export type TimeRange =
  | "1M"
  | "3M"
  | "6M"
  | "YTD"
  | "1Y"
  | "3Y"
  | "5Y"
  | "7Y"
  | "10Y"
  | "ALL";

interface BacktestState {
  result: BacktestResult | null;
  isLoading: boolean;
  error: string | null;
  timeRange: TimeRange;
  strategyCode: string;
  setResult: (result: BacktestResult | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setTimeRange: (range: TimeRange) => void;
  setStrategyCode: (code: string) => void;
  reset: () => void;
}

export const useBacktestStore = create<BacktestState>((set) => ({
  result: null,
  isLoading: false,
  error: null,
  timeRange: "1Y",
  strategyCode: "",
  setResult: (result) => set({ result, error: null }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setTimeRange: (timeRange) => set({ timeRange }),
  setStrategyCode: (strategyCode) => set({ strategyCode }),
  reset: () => set({ result: null, error: null, isLoading: false }),
}));
